import { useEffect, useMemo, useState } from 'react';
import type { SnapshotsSections } from './SnapshotTypes';
import { getSectionTitleBySnapshot } from './utils';

export interface UseSectionStateOptions {
  sections: SnapshotsSections;
  selectedSnapshot?: string[] | null;
}

export interface UseSectionStateReturn {
  openedSections: { [key: string]: boolean };
  handleSectionToggle: (sectionTitle: string) => void;
  handleSearchStateChange: (value: string) => void;
}

export const useSectionState = ({
  sections,
  selectedSnapshot,
}: UseSectionStateOptions): UseSectionStateReturn => {
  const initialOpenedSections = useMemo(
    () =>
      sections.reduce(
        (acc, section) => {
          acc[section.title] = section.opened;
          return acc;
        },
        {} as { [key: string]: boolean },
      ),
    [sections],
  );

  const [openedSections, setOpenedSections] = useState<{
    [key: string]: boolean;
  }>(initialOpenedSections);

  // Ensure that the section containing the selected snapshots are opened when selected snapshot changes
  useEffect(() => {
    if (selectedSnapshot) {
      const opened: string[] = [];
      // find all sections that contain selected snapshots and add them to opened sections
      for (const selectedSnapshotId of selectedSnapshot) {
        const sectionTitle = getSectionTitleBySnapshot(
          sections,
          selectedSnapshotId,
        );
        if (sectionTitle && !opened.includes(sectionTitle)) {
          opened.push(sectionTitle);
        }
      }
      // Update opened sections state with the new sections
      if (opened.length) {
        setOpenedSections((prev) => ({
          ...prev, // if section is already opened (e.g. manually), keep it opened
          ...opened.reduce(
            (acc, title) => {
              acc[title] = true;
              return acc;
            },
            {} as { [key: string]: boolean },
          ),
        }));
      }
    }
  }, [selectedSnapshot, sections]);

  const handleSectionToggle = (sectionTitle: string) => {
    setOpenedSections((prev) => ({
      ...prev,
      [sectionTitle]: !prev[sectionTitle],
    }));
  };

  const handleSearchStateChange = (value: string) => {
    // If search value is set, open all sections to show all results
    if (value) {
      setOpenedSections((prev) => {
        const newSections = { ...prev };
        for (const section of sections) {
          newSections[section.title] = true;
        }
        return newSections;
      });
    } else {
      // Reset opened sections to initial state when search is cleared
      setOpenedSections(initialOpenedSections);
    }
  };

  return {
    openedSections,
    handleSectionToggle,
    handleSearchStateChange,
  };
};
